import React, { useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useSelector, useDispatch } from 'react-redux';
import { 
  Layers,
  Search,
  ChevronDown,
  User,
  Settings,
  LogOut,
  Menu,
  X,
  SunMedium,
  Moon
} from 'lucide-react';
import type { RootState, AppDispatch } from '@/store';
import { logout } from '@/store/slices/authSlice';
import { usePermissions } from '@/hooks/usePermissions';
import { Permission } from '@/utils/permissions';
import NotificationDropdown from '@/components/ui/NotificationDropdown';
import Input from '@/components/ui/Input';
import Button from '@/components/ui/Button';
import { useTheme } from '@/hooks/useTheme';
import GlobalSearchBar from './GlobalSearchBar';

interface NavigationItem {
  name: string;
  href: string;
  children?: NavigationItem[];
  permission?: Permission;
  anyOf?: Permission[];
}

const NavigationBar: React.FC = () => {
  const location = useLocation();
  const dispatch = useDispatch<AppDispatch>();
  const { user } = useSelector((state: RootState) => state.auth);
  const { can, canAny } = usePermissions();
  const [isUserMenuOpen, setIsUserMenuOpen] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const { theme, toggleTheme } = useTheme();

  const handleLogout = () => {
    dispatch(logout());
  };

  const navigation: NavigationItem[] = [
    { name: 'Dashboard', href: '/dashboard' },
    {
      name: 'Planning',
      href: '/planning',
      permission: Permission.VIEW_PLANNING_PERIODS,
      children: [
        {
          name: 'Planning Periods',
          href: '/planning-periods',
          permission: Permission.VIEW_PLANNING_PERIODS
        },
        {
          name: 'Scenarios',
          href: '/scenarios',
          permission: Permission.VIEW_SCENARIOS
        },
        {
          name: 'Dependencies',
          href: '/dependencies',
          permission: Permission.VIEW_DEPENDENCIES
        }
      ]
    },
    {
      name: 'Portfolio',
      href: '/portfolio',
      anyOf: [Permission.VIEW_INVESTIBLE_STRUCTURES, Permission.VIEW_EPICS, Permission.VIEW_FEATURES],
      children: [
        {
          name: 'Investible Structures',
          href: '/investible-structures',
          permission: Permission.VIEW_INVESTIBLE_STRUCTURES
        },
        {
          name: 'EPICs',
          href: '/epics',
          permission: Permission.VIEW_EPICS
        },
        {
          name: 'Features',
          href: '/features',
          permission: Permission.VIEW_FEATURES
        },
        {
          name: 'OKRs',
          href: '/okrs',
          permission: Permission.VIEW_EPICS
        }
      ]
    },
    {
      name: 'Delivery',
      href: '/delivery',
      anyOf: [Permission.VIEW_PROGRAMS, Permission.VIEW_TEAMS, Permission.VIEW_ALLOCATIONS],
      children: [
        {
          name: 'Programs',
          href: '/programs',
          permission: Permission.VIEW_PROGRAMS
        },
        {
          name: 'Teams',
          href: '/teams',
          permission: Permission.VIEW_TEAMS
        },
        {
          name: 'Allocations',
          href: '/allocations',
          permission: Permission.VIEW_ALLOCATIONS
        }
      ]
    },
    {
      name: 'Reports',
      href: '/reports',
      permission: Permission.VIEW_REPORTS,
      children: [
        {
          name: 'Executive Dashboard',
          href: '/reports/executive',
          permission: Permission.VIEW_EXECUTIVE_DASHBOARD
        },
        {
          name: 'IS Owner Dashboard',
          href: '/reports/is-owner',
          permission: Permission.VIEW_IS_OWNER_DASHBOARD
        },
        {
          name: 'Program Lead Dashboard',
          href: '/reports/program-lead',
          permission: Permission.VIEW_PROGRAM_LEAD_DASHBOARD
        },
        {
          name: 'Capacity Reports',
          href: '/reports/capacity',
          permission: Permission.VIEW_CAPACITY
        }
      ]
    },
    {
      name: 'Admin',
      href: '/admin',
      anyOf: [Permission.VIEW_USERS, Permission.MANAGE_USERS, Permission.VIEW_AUDIT_LOGS],
      children: [
        {
          name: 'User Management',
          href: '/users',
          permission: Permission.VIEW_USERS
        }
      ]
    }
  ];

  const isActiveRoute = (href: string) => {
    if (href === '/dashboard') {
      return location.pathname === '/dashboard';
    }
    return location.pathname.startsWith(href);
  };

  const hasActiveChild = (item: NavigationItem) => {
    if (!item.children) return false;
    return item.children
      .filter((child) => hasPermissionForItem(child))
      .some((child) => isActiveRoute(child.href));
  };

  const hasPermissionForItem = (item: NavigationItem): boolean => {
    if (item.permission) {
      return can(item.permission);
    }
    if (item.anyOf) {
      return canAny(item.anyOf);
    }
    return true;
  };

  const getVisibleChildren = (item: NavigationItem): NavigationItem[] => {
    if (!item.children) return [];
    return item.children.filter((child) => hasPermissionForItem(child));
  };

  return (
    <nav className="glass-nav sticky top-0 z-50 h-16">
      <div className="h-full px-4 sm:px-6 lg:px-8">
        <div className="flex items-center h-full gap-4">
          {/* Logo */}
          <Link to="/dashboard" className="flex items-center gap-3 group">
            <div className="relative w-10 h-10 rounded-xl flex items-center justify-center overflow-hidden transition-all duration-300 group-hover:scale-105"
              style={{
                background: 'linear-gradient(135deg, rgba(59, 130, 246, 0.15) 0%, rgba(37, 99, 235, 0.25) 100%)',
                border: '1px solid rgba(59, 130, 246, 0.3)',
                boxShadow: '0 4px 12px -2px rgba(59, 130, 246, 0.2), inset 0 1px 0 rgba(255, 255, 255, 0.2)'
              }}>
              <Layers className="w-5 h-5 text-blue-500 dark:text-blue-400" />
              <div className="absolute inset-0 bg-gradient-to-br from-white/20 to-transparent pointer-events-none" />
            </div>
            <div className="hidden sm:block">
              <span className="text-base font-semibold tracking-tight bg-gradient-to-r from-foreground to-muted-foreground bg-clip-text text-transparent">
                Portfolio Manager
              </span>
              <span className="block text-[10px] uppercase tracking-wider text-muted-foreground">
                Enterprise Planning
              </span>
            </div>
          </Link>

          {/* Spacer */}
          <div className="flex-1" />

          {/* Search */}
          <div className="hidden md:block w-72">
            <GlobalSearchBar />
          </div>

          {/* Theme Toggle */}
          <button
            onClick={toggleTheme}
            className="relative p-2.5 rounded-xl transition-all duration-200 hover:scale-105 group"
            style={{
              background: theme === 'light'
                ? 'rgba(59, 130, 246, 0.08)'
                : 'rgba(59, 130, 246, 0.15)',
              border: '1px solid rgba(59, 130, 246, 0.2)'
            }}
            aria-label="Toggle theme"
          >
            {theme === 'light' ? (
              <Moon className="w-5 h-5 text-blue-600 group-hover:text-blue-700" />
            ) : (
              <SunMedium className="w-5 h-5 text-blue-400 group-hover:text-blue-300" />
            )}
          </button>

          {/* Notifications */}
          <NotificationDropdown />

          {/* User Menu */}
          <div className="relative">
            <button
              onClick={() => setIsUserMenuOpen(!isUserMenuOpen)}
              className="flex items-center gap-3 p-2 rounded-xl transition-all duration-200 hover:bg-blue-500/5 dark:hover:bg-blue-400/10"
            >
              <div
                className="relative w-9 h-9 rounded-xl flex items-center justify-center text-xs font-bold overflow-hidden"
                style={{
                  background: 'linear-gradient(135deg, #3b82f6 0%, #2563eb 50%, #1d4ed8 100%)',
                  color: 'white',
                  boxShadow: '0 2px 8px -2px rgba(59, 130, 246, 0.4)'
                }}
              >
                {user?.firstName?.[0]}
                {user?.lastName?.[0]}
                <div className="absolute inset-0 bg-gradient-to-br from-white/20 to-transparent pointer-events-none" />
              </div>
              <div className="hidden sm:block text-left">
                <div className="text-sm font-semibold text-foreground">
                  {user?.firstName} {user?.lastName}
                </div>
                <div className="text-xs text-muted-foreground">{user?.role}</div>
              </div>
              <ChevronDown className="w-4 h-4 text-muted-foreground" />
            </button>

            {isUserMenuOpen && (
              <div className="glass-dropdown absolute right-0 mt-2 w-56 py-2 z-50">
                <Link
                  to="/profile"
                  className="glass-dropdown-item"
                  onClick={() => setIsUserMenuOpen(false)}
                >
                  <User className="w-4 h-4" />
                  Profile
                </Link>
                <Link
                  to="/settings"
                  className="glass-dropdown-item"
                  onClick={() => setIsUserMenuOpen(false)}
                >
                  <Settings className="w-4 h-4" />
                  Settings
                </Link>
                <hr className="my-2 border-[var(--glass-border-subtle)]" />
                <button
                  onClick={handleLogout}
                  className="glass-dropdown-item w-full text-left"
                  style={{ color: 'var(--accent-rose)' }}
                >
                  <LogOut className="w-4 h-4" />
                  Sign out
                </button>
              </div>
            )}
          </div>

          {/* Mobile Menu Button */}
          <button
            onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
            className="md:hidden p-2.5 rounded-xl transition-all duration-200 hover:bg-blue-500/10 dark:hover:bg-blue-400/15"
          >
            {isMobileMenuOpen ? (
              <X className="w-5 h-5 text-muted-foreground" />
            ) : (
              <Menu className="w-5 h-5 text-muted-foreground" />
            )}
          </button>
        </div>

        {/* Mobile Menu */}
        {isMobileMenuOpen && (
          <div className="glass-mobile-menu md:hidden absolute left-0 right-0 top-16 p-4">
            <div className="space-y-1">
              {navigation
                .filter((item) => hasPermissionForItem(item))
                .map((item) => {
                  const visibleChildren = getVisibleChildren(item);

                  return (
                    <div key={item.name}>
                      <Link
                        to={item.href}
                        className={`block px-4 py-3 text-base font-medium rounded-xl transition-all ${
                          isActiveRoute(item.href) || hasActiveChild(item)
                            ? 'bg-[var(--accent-blue-soft)]'
                            : 'hover:bg-surface-muted/50'
                        }`}
                        style={{
                          color: isActiveRoute(item.href) || hasActiveChild(item)
                            ? 'var(--accent-blue)'
                            : 'var(--text-secondary)'
                        }}
                        onClick={() => setIsMobileMenuOpen(false)}
                      >
                        {item.name}
                      </Link>
                      {visibleChildren.length > 0 && (
                        <div className="ml-4 space-y-1 mt-1">
                          {visibleChildren.map((child) => (
                            <Link
                              key={child.name}
                              to={child.href}
                              className={`block px-4 py-2.5 text-sm rounded-lg transition-all ${
                                isActiveRoute(child.href)
                                  ? 'bg-[var(--accent-blue-soft)]'
                                  : 'hover:bg-surface-muted/50'
                              }`}
                              style={{
                                color: isActiveRoute(child.href)
                                  ? 'var(--accent-blue)'
                                  : 'var(--text-muted)'
                              }}
                              onClick={() => setIsMobileMenuOpen(false)}
                            >
                              {child.name}
                            </Link>
                          ))}
                        </div>
                      )}
                    </div>
                  );
                })}
            </div>
          </div>
        )}
      </div>
    </nav>
  );
};

export default NavigationBar;
